/**
 * File-provenance lookup (BB-FILE-DROP-01, contract Appendix B.2 `/extractions`)
 * — feeds NeedsAttentionQueue's file chips. Read-only, cache-friendly: a plain
 * TanStack query, same shape as useBrandBible's key convention.
 */
import { useQuery } from '@tanstack/react-query';
import { bbFilesService } from '@/services/bbFilesService';
import { fromApiTable } from '@/services/brandBibleV2Service';
import type { ProvenanceMap } from '@/components/brand-bible/NeedsAttentionQueue';

export const bbExtractionsKeys = {
  list: (tenantId: string) => ['bb-extractions', tenantId] as const,
};

export function useBbExtractions(tenantId: string | null) {
  return useQuery({
    queryKey: bbExtractionsKeys.list(tenantId ?? ''),
    queryFn: async (): Promise<ProvenanceMap> => {
      if (!tenantId) return new Map();
      try {
        const { extractions } = await bbFilesService.listExtractions(tenantId);
        const map: ProvenanceMap = new Map();
        for (const ex of extractions) {
          // table_name is the DB name ("bb_services"); AttentionRow keys use the
          // UI's short name — normalize through the one shared seam.
          map.set(`${fromApiTable(ex.table_name)}:${ex.entry_id}`, {
            filename: ex.filename,
            quote: ex.quote,
            file_id: ex.file_id,
          });
        }
        return map;
      } catch {
        // The endpoint can 404/500 (e.g. a missing migration) or fail on the
        // network. Provenance is a nice-to-have chip, not core data — degrade
        // to "absent" (empty map) rather than surfacing error-noise on mount.
        // No `retry` option: swallowing here means react-query always sees
        // SUCCESS, so a retry would be dead code — staleTime expiry is what
        // eventually refetches once the backend recovers.
        return new Map();
      }
    },
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}
